import { TestPackagePage } from "@/pages/TestPackagePage";

/**
 * AWARE Tank 360 — the primary commercial landing page for
 * "rainwater testing" search intent.
 *
 * Jun 2026 (SEO brief): strengthened the title / meta / H1 and opening
 * copy around *rainwater testing*, added two informational sections and
 * expanded the FAQ. Deliberately NOT a new /rainwater-testing page —
 * this page stays the single commercial target for that phrase
 * (/aware-4-point = entry-level screening, location pages = local
 * intent, /blog = informational intent).
 *
 * Copy rule: no blanket "test your rainwater every year" claim.
 * Australian public-health guidance does not require routine testing of
 * domestic tanks, so the copy frames testing around situations where it
 * is genuinely useful.
 */
const FAQS = [
  {
    q: "What is rainwater testing?",
    a: "Rainwater testing is laboratory analysis of a water sample taken from your rainwater tank or the tap it feeds. A lab measures indicators you cannot see or taste — bacterial indicators, acidity, minerals, salts and metals — and reports them against the Australian Drinking Water Guidelines so you can see how your tank water is actually performing.",
  },
  {
    q: "What does the AWARE Tank 360 include?",
    a: "The AWARE Tank 360 is our most comprehensive rainwater tank package. It covers E. coli and Coliforms, pH, TDS, conductivity, turbidity, temperature, alkalinity, total and calcium hardness, calcium and magnesium, chloride, nitrate, ammonia, phosphate and sulphate, plus iron and copper. Every result arrives with a plain-English summary and practical recommendations.",
  },
  {
    q: "When should I consider testing my rainwater tank?",
    a: "Testing is useful when you want to understand what is happening in your tank water, or when there is a specific concern. Common triggers include moving into a property on tank water, buying or selling a rural property, a change in taste, odour, colour or staining, suspected contamination of the roof catchment or tank, a long gap since the tank was last inspected or cleaned, and changes to plumbing, filtration or treatment equipment. Australian guidance does not require routine testing of every domestic tank, so we would rather help you test for a reason than test for the sake of it.",
  },
  {
    q: "Can you test rainwater for E. coli?",
    a: "Yes. Every AWARE package includes E. coli and Coliforms as presence/absence results. E. coli indicates recent faecal contamination — often bird or animal droppings entering via the roof or an unscreened inlet — and any detection is reported as not meeting the Australian Drinking Water Guidelines, with the practical steps to address it.",
  },
  {
    q: "How do I know if my rainwater is suitable for drinking?",
    a: "You cannot tell from appearance alone — clear, good-tasting tank water can still carry bacterial indicators. A laboratory test tells you how your sample compares against the Australian Drinking Water Guidelines on the day it was collected. That result, read together with your tank condition, catchment, plumbing and any treatment equipment, is what tells you whether your supply is suitable for drinking or needs work.",
  },
  {
    q: "Does rainwater testing check for heavy metals?",
    a: "The AWARE Tank 360 includes iron and copper, the metals most commonly picked up from roofing, flashing, fittings and plumbing on a rainwater system. If you need targeted analysis of other metals such as lead or zinc, contact us — we can advise on extended panels for your situation.",
  },
  {
    q: "How do I collect a rainwater sample correctly?",
    a: "Use the sterile bottles supplied in your kit, collect from the tap the household actually drinks from, do not rinse the bottle or touch the inside of the lid, fill to the marked line and keep the sample cool. Then post it back or drop it in. Full step-by-step instructions come with your booking confirmation and in the kit.",
  },
  {
    q: "Can I send a rainwater sample from anywhere in Australia?",
    a: "Yes. Mail-out sample kits are available Australia-wide — your kit arrives by post with the bottles, instructions and return packaging. Local drop-off is also available at our Noosaville laboratory (Unit 1/37 Gateway Drive) during business hours if you are nearby.",
  },
  {
    q: "What is the difference between AWARE 4-Point and AWARE Tank 360?",
    a: "The AWARE 4-Point is an entry-level screen — E. coli, Coliforms, pH and conductivity — for a quick answer on a private supply. The AWARE Tank 360 is the comprehensive rainwater option: it adds turbidity, hardness, alkalinity, minerals, salts and metals, so you can see system-level issues such as corrosion, sediment, staining and mineral imbalance rather than bacteria alone.",
  },
  {
    q: "Can I drop my sample off in person?",
    a: "Yes — our Noosaville laboratory accepts sample drop-offs during business hours. Your booking confirmation includes the address, drop-off hours and sampling instructions.",
  },
];

const SECTIONS = [
  {
    title: "When is rainwater testing worth considering?",
    body:
      "Testing can be useful when you want to understand what is happening in your tank water, or when there is a specific water-quality concern. Australian guidance does not require every household to test its rainwater on a set schedule, so these are the situations where a laboratory result genuinely changes what you do next.",
    items: [
      "Moving into a property that relies on rainwater for household use",
      "Buying or selling a rural or lifestyle property on tank supply",
      "After suspected contamination of the roof catchment or the tank itself",
      "Unexplained taste, odour, colour, cloudiness or staining",
      "After a long period without tank inspection, cleaning or gutter maintenance",
      "After plumbing, filtration or treatment system changes",
      "Where the household relies on rainwater as its primary drinking supply",
      "When you are already investigating a known water-quality problem",
    ],
    footnote:
      "A test describes the sample received on the day it was collected. Read it alongside your tank condition, catchment and treatment setup — your report includes recommendations for exactly that.",
  },
  {
    title: "What can affect rainwater quality?",
    body:
      "Rainwater arrives clean and then picks up whatever it meets on the way into, and inside, your tank. Knowing the common influences makes a laboratory result much easier to act on.",
    items: [
      "Roof catchment material, condition and overhanging trees",
      "Gutters holding leaves, debris and sediment",
      "Bird and animal droppings, and unscreened inlets or overflows",
      "First-flush diverters that are missing, blocked or undersized",
      "Sediment and sludge built up in the base of the tank",
      "Bushfire ash, smoke residue and other airborne fallout",
      "Groundwater or surface water entering damaged or in-ground tanks",
      "Plumbing, fittings and metal components in contact with the water",
      "Long dry spells, low tank levels and stagnant water",
    ],
  },
];

const LEARN_LINKS = [
  { to: "/blog/is-rainwater-safe-to-drink", label: "Is rainwater safe to drink?" },
  { to: "/blog/how-to-get-rainwater-tested-a-2026-australian-homeowners-guide", label: "How to get rainwater tested" },
  { to: "/blog/e-coli-in-rainwater-tank-symptoms-a-2026-guide-for-australian-homeowners", label: "E. coli in rainwater tanks" },
  { to: "/blog/effects-of-roof-material-on-rainwater-quality-the-homeowners-myth-busting-guide", label: "Roof material & rainwater quality" },
];

export default function EssentialTankWaterTestPage() {
  return (
    <TestPackagePage
      testId="tank"
      seo={{
        title: "Rainwater Testing Australia | AWARE Tank 360 Water Test",
        description:
          "Rainwater testing for Australian homes — AWARE Tank 360 laboratory testing of rainwater tank water. Australia-wide kits, plain-English results.",
        urlPath: "/aware-tank-360",
      }}
      intro={{
        h1: "AWARE Tank 360 — Comprehensive Rainwater Testing",
        lede:
          "Comprehensive rainwater testing for Australian households that live off a rainwater tank. The AWARE Tank 360 analyses your tank water across microbiological, chemical, mineral and metal indicators — so instead of guessing from taste, colour or clarity, you get a laboratory result and a plain-English explanation of what it means for your home.",
      }}
      whoFor={
        "The AWARE Tank 360 is built for Australian rainwater tank owners who want a more complete understanding of their water rather than a single yes-or-no answer. It is the package we recommend where a household drinks, cooks and bathes in tank water, where a property has changed hands, or where something has changed in the taste, appearance or performance of the supply. Alongside E. coli and Coliforms, it measures pH, turbidity, hardness, alkalinity, dissolved salts, key minerals and the metals a rainwater system most commonly picks up from roofing and plumbing — the difference between knowing your water carries bacteria and understanding why. Sample kits are posted Australia-wide, with local drop-off available at our Noosaville laboratory, and every report is written in plain English with practical recommendations for your next step."
      }
      sections={SECTIONS}
      learnLinks={LEARN_LINKS}
      faqs={FAQS}
    />
  );
}
